/**
 * @file Send links that leave the page to a new tab, and say so.
 *
 * Two kinds of link leave: one that goes off-site, and one that opens a
 * document rather than a page (see document-links.js, which names the file
 * type). They are decided together here because they are one rule — the
 * visitor is taken somewhere the site's navigation and the back button no
 * longer serve them — and because the notice and the `rel` are the same
 * either way.
 *
 * Applied to every link on the page rather than being something an editor sets
 * per link: the behaviour is a site-wide rule, and the text formats deliberately
 * do not expose a target attribute (`editor_advanced_link_link` ships with no
 * enabled attributes, and `filter_html` allows neither `target` nor `rel` on an
 * `<a>`), so there is no editor-facing switch to keep in sync with it.
 *
 * Done in the browser because links reach a page from too many places to catch
 * server-side — body text, link fields on paragraphs and cards, menus, the
 * footer — and every one of them would need its own hook. A visitor without JS
 * follows the link in the same tab, which is the behaviour of an ordinary link
 * and loses them nothing. (The contributed `extlink` module solves it the same
 * way; it is not used here because its per-site settings UI and link icon are
 * more than this needs.)
 *
 * A link opening a new tab must announce it, or a screen reader user is moved
 * without warning and the back button no longer goes back (WCAG 3.2.5). The
 * notice therefore goes on every link that ends up with `target="_blank"` —
 * including the few that arrive with one already, such as the Remote video
 * links Linkit builds (see MediaFileOrRemoteUrl) — and not only on the ones
 * this module retargets.
 */

/* global Drupal, once */

import { deliberateTarget, isExternalUrl } from "./_helpers.js";
import { isDocumentUrl } from "./document-links.js";

/**
 * Matches an accessible name that already announces the new tab.
 *
 * Some links say it in their own markup — the social icons pass an aria-label
 * ending "opens in a new tab" — and those must not be told twice.
 */
const ANNOUNCED = /new (tab|window)/i;

/**
 * Returns whether following a link takes the visitor out of this page.
 *
 * The `download` test applies to the document ground only, and the asymmetry
 * is the browsers': a same-origin download writes the file to disk and leaves
 * the page where it is, so there is no new tab and nothing to announce, while
 * `download` on a cross-origin link is ignored for security and the link
 * navigates like any other.
 *
 * @param {HTMLAnchorElement} link - The link to test.
 * @returns {boolean}
 */
function leavesThePage(link) {
  if (isExternalUrl(link.href)) {
    return true;
  }
  return isDocumentUrl(link.href) && !link.hasAttribute("download");
}

/**
 * Adds the new-tab notice to a link's accessible name.
 *
 * @param {HTMLAnchorElement} link - The link to annotate.
 */
function announce(link) {
  const label = link.getAttribute("aria-label");

  /* An aria-label replaces the link's contents for assistive technology, so on
     a labelled link (an icon-only one, typically) the notice has to join the
     label — appended as text it would never be read. */
  if (label !== null) {
    link.setAttribute(
      "aria-label",
      `${label}, ${Drupal.t("opens in a new tab")}`,
    );
    return;
  }

  const notice = document.createElement("span");
  /* `visually-hidden` (Drupal core's system/base, on every page) rather than
     the `sr-only` the twig templates use: Tailwind generates a utility only
     where it can see the literal class name, and its content globs do not
     reach this directory — `sr-only` written here would be purged. */
  notice.className = "visually-hidden";
  notice.textContent = ` ${Drupal.t("(opens in a new tab)")}`;
  link.appendChild(notice);
}

/**
 * Retargets and annotates the links that leave the page within a context.
 *
 * Idempotent: elements are collected with once(), so repeat attaches — and the
 * notice — happen a single time per link.
 *
 * @param {HTMLElement|Document} context
 *   The scope to search (document on full page load, the inserted wrapper on
 *   Drupal AJAX updates).
 */
export function externalLinks(context) {
  once("ts-cswe-external-link", "a[href]", context).forEach((link) => {
    const target = deliberateTarget(link);

    /* A target the markup chose wins: it is a decision about that one link,
       made with more context than a URL has. Not every target attribute is
       such a choice — @see deliberateTarget(). */
    if (target && target !== "_blank") {
      return;
    }

    if (!target) {
      if (!leavesThePage(link)) {
        return;
      }
      link.setAttribute("target", "_blank");
    }

    /* Belt and braces: current browsers imply noopener for target="_blank",
       older ones hand the new tab a window.opener it can navigate this one
       with. Any rel the link already carries (nofollow, me) is kept. */
    const rel = (link.getAttribute("rel") || "").split(/\s+/).filter(Boolean);
    if (!rel.includes("noopener")) {
      link.setAttribute("rel", [...rel, "noopener"].join(" "));
    }

    if (!ANNOUNCED.test(link.getAttribute("aria-label") ?? link.textContent)) {
      announce(link);
    }
  });
}
